/**
 * Modern Bold · Wishlist
 * ─────────────────────────────────────────────────────────────────────────
 * Shared wishlist primitive — used on PDP, collection, search, and any
 * other surface that renders product cards. Loaded by each section that
 * needs it (no global theme.liquid include).
 *
 *   <mb-wishlist-button data-product-handle="...">   autonomous custom
 *                                                    element with
 *                                                    role="button" +
 *                                                    Enter/Space support
 *
 * Storage: localStorage key `mb_wishlist` (JSON array of handles).
 * Event:   document dispatches `mb:wishlist-updated` { detail.wishlist }
 *          on every toggle.
 *
 * Note on double-load: classic <script> tags in the same page share the
 * global `let`/`const` lexical environment. If this file is included by
 * two sections (e.g. pdp-grid-product-detail + pdp-grid-gallery), a
 * top-level `const WL_KEY` would re-declare on the second run and throw
 * a SyntaxError that aborts the script. We sidestep that by putting all
 * declarations INSIDE the customElements guard — the second run finds
 * the class already defined, skips the whole block, and nothing is
 * re-declared.
 */
if (!customElements.get('mb-wishlist-button')) {
  const WL_KEY = 'mb_wishlist';

  const getWishlist = () => {
    try { return JSON.parse(localStorage.getItem(WL_KEY) || '[]'); } catch { return []; }
  };
  const saveWishlist = (list) => {
    try { localStorage.setItem(WL_KEY, JSON.stringify(list)); } catch { /* quota / private mode */ }
  };

  class MBWishlistButton extends HTMLElement {
    #onClick = null;
    #onKeydown = null;

    connectedCallback() {
      const handle = this.dataset.productHandle;
      if (!handle) return;

      // Defensive — Liquid markup already sets these but a re-mounted
      // element (theme editor section reload) or a hand-written instance
      // may not have them.
      if (!this.hasAttribute('role'))     this.setAttribute('role', 'button');
      if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
      this.setAttribute('aria-pressed', String(getWishlist().includes(handle)));

      this.#onClick = () => this.#toggle(handle);
      this.#onKeydown = (e) => {
        // Enter and Space are the canonical activation keys for a role=button.
        // e.code === 'Space' is a defensive fallback for layouts that report
        // Space as something other than the spec's e.key === ' '.
        if (e.key === 'Enter' || e.key === ' ' || e.code === 'Space') {
          e.preventDefault();
          this.#toggle(handle);
        }
      };

      this.addEventListener('click',   this.#onClick);
      this.addEventListener('keydown', this.#onKeydown);
    }

    disconnectedCallback() {
      if (this.#onClick)   this.removeEventListener('click',   this.#onClick);
      if (this.#onKeydown) this.removeEventListener('keydown', this.#onKeydown);
    }

    #toggle(handle) {
      const wl = getWishlist();
      const idx = wl.indexOf(handle);
      if (idx === -1) {
        wl.push(handle);
        this.setAttribute('aria-pressed', 'true');
      } else {
        wl.splice(idx, 1);
        this.setAttribute('aria-pressed', 'false');
      }
      saveWishlist(wl);
      document.dispatchEvent(new CustomEvent('mb:wishlist-updated', { detail: { wishlist: wl } }));
    }
  }

  customElements.define('mb-wishlist-button', MBWishlistButton);
}
